import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getUtmColumns } from "@/hooks/useUtmPersistence";

const TRACKED_KEY = "pv_tracked";

function extractDomain(url: string): string {
  try {
    return new URL(url).hostname.replace("www.", "");
  } catch {
    return "direct";
  }
}

function categorizeReferrer(domain: string): string {
  if (!domain || domain === "direct") return "Direct";
  if (domain.includes("google")) return "Google";
  if (domain.includes("reddit")) return "Reddit";
  if (domain.includes("twitter") || domain.includes("x.com") || domain.includes("t.co")) return "X / Twitter";
  if (domain.includes("facebook") || domain.includes("fb.com")) return "Facebook";
  if (domain.includes("youtube")) return "YouTube";
  if (domain.includes("bing")) return "Bing";
  return domain;
}

export function useTrackPageView() {
  const tracked = useRef(false);

  useEffect(() => {
    if (tracked.current) return;
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(TRACKED_KEY)) return;
    tracked.current = true;
    sessionStorage.setItem(TRACKED_KEY, "1");

    const referrer = document.referrer;
    const referrerDomain = referrer ? extractDomain(referrer) : "direct";

    // Don't track same-site referrals
    if (referrerDomain === window.location.hostname.replace("www.", "")) return;

    let sessionId = sessionStorage.getItem("ab_session_id");
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      sessionStorage.setItem("ab_session_id", sessionId);
    }

    // Pull from centralized UTM store — captures first-touch correctly even if
    // the user landed on a different page before this hook mounted.
    const utmCols = getUtmColumns();

    supabase.from("page_views").insert({
      session_id: sessionId,
      landing_page: window.location.pathname,
      referrer_domain: categorizeReferrer(referrerDomain),
      referrer_full: referrer || null,
      utm_source: utmCols.utm_source || null,
      utm_medium: utmCols.utm_medium || null,
      utm_campaign: utmCols.utm_campaign || null,
    } as any).then(() => {});
  }, []);
}
